import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { MdsAddCircle, MdsArrowSplit, MdsCopyAll, MdsDelete, MdsMoreVert, MdsPageHeader } from "@sinch/icons";
import { isNilOrEmpty } from "ramda-adjunct";
import React, { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRateTreeContext } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTreeProvider";
import { useAddTreeNode } from "@sinch/components/application/RatelistBuilder/hooks/UseAddTreeNode";
import { RateBuilderTreeFormFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { RateBuilderTreeViewItem } from "@sinch/components/application/RatelistBuilder/types";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";

interface RowMenuProps {
  rowId: string;
}
export const RowMenu: FC<RowMenuProps> = ({ rowId }) => {
  const { t } = useTranslation();
  const { remove, copy, getParent } = useRateTreeContext<RateBuilderTreeViewItem>();
  const { disableConditions, disableNesting } = useRateBuilderSettings();
  const { addCondition, addElseNode } = useAddTreeNode(rowId, disableConditions);
  const { setValue } = useFormContext<RateBuilderTreeFormFields>();
  const name = useWatch<RateBuilderTreeFormFields>({ name: `Rate.${rowId}.name` });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip disableFocusListener title={t("moreActions")}>
        <IconButton data-cy="RateBuilder-row-menu" disableRipple={false} onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MdsIcon color="info" fontSize="small" icon={MdsMoreVert} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        onClose={() => setAnchorEl(null)}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        {!disableNesting && (
          <>
            <MenuItem
              data-cy="RateBuilder-add-nested"
              onClick={() => {
                addCondition();
                setAnchorEl(null);
              }}
            >
              <ListItemIcon>
                <MdsIcon fontSize="small" icon={MdsAddCircle} />
              </ListItemIcon>
              <ListItemText>{t("RateBuilder.addNestedRate")}</ListItemText>
            </MenuItem>
            <MenuItem
              data-cy="RateBuilder-add-else-node"
              onClick={() => {
                addElseNode();
                setAnchorEl(null);
              }}
            >
              <ListItemIcon>
                <MdsIcon fontSize="small" icon={MdsArrowSplit} />
              </ListItemIcon>
              <ListItemText>{t("RateBuilder.addElseNode")}</ListItemText>
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem
          data-cy="RateBuilder-delete-node"
          onClick={() => {
            const parent = getParent(rowId);
            remove(rowId);
            if (parent?.type === "wrapper" && parent?.children?.length === 1) {
              remove(parent.id);
            }
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <MdsIcon fontSize="small" icon={MdsDelete} />
          </ListItemIcon>
          <ListItemText>{t("delete")}</ListItemText>
        </MenuItem>
        <MenuItem
          data-cy="RateBuilder-copy-node"
          onClick={() => {
            copy(rowId);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <MdsIcon fontSize="small" icon={MdsCopyAll} />
          </ListItemIcon>
          <ListItemText>{t("Action.copy")}</ListItemText>
        </MenuItem>
        {isNilOrEmpty(name) && (
          <MenuItem
            onClick={() => {
              setValue(`Rate.${rowId}.name`, `New rate`);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <MdsIcon fontSize="small" icon={MdsPageHeader} />
            </ListItemIcon>
            <ListItemText>{t("RateBuilder.addName")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
